import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  ViewList as ListIcon,
  AccountTree as TreeIcon
} from '@mui/icons-material';
import StageNode from './StageNode';
import StageEditorPanel from './StageEditorPanel';
import { databaseToVisualState, visualStateToDatabase, validateFlowStructure, createNewStage } from '../../../utils/transformations/flowTransformations';

const nodeTypes = {
  stageNode: StageNode
};

const VisualFlowEditor = ({ flow, onFlowChange, onValidationChange }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  // Transform database flow to visual state on mount/update
  // Re-sync whenever flow stages change to keep form and visual views in sync
  useEffect(() => {
    if (flow && flow.stages) {
      const { nodes: flowNodes, edges: flowEdges } = databaseToVisualState(flow);
      setNodes(flowNodes);
      setEdges(flowEdges);
    }
  }, [flow._id, flow.stages]); // Re-transform when flow ID or stages change

  // Validate flow whenever stages change
  useEffect(() => {
    if (flow) {
      const validation = validateFlowStructure(flow);
      setValidationErrors(validation.errors);
      if (onValidationChange) {
        onValidationChange(validation);
      }
    }
  }, [flow, onValidationChange]);

  const handleAddStage = useCallback(() => {
    const newStageNumber = flow.stages.length + 1;
    const newStage = createNewStage(newStageNumber);

    const updatedStages = [...flow.stages, newStage];
    onFlowChange({ ...flow, stages: updatedStages });

    // Also add to visual state
    const newNode = {
      id: `stage-${newStageNumber}`,
      type: 'stageNode',
      position: {
        x: (newStageNumber - 1) * 320,
        y: 100
      },
      data: {
        stage: newStage,
        onEdit: () => setSelectedStage(newStage),
        onDelete: () => handleDeleteStage(newStageNumber)
      }
    };

    setNodes(nds => [...nds, newNode]);

    // Add edge from previous stage
    if (flow.stages.length > 0) {
      const newEdge = {
        id: `edge-${newStageNumber - 1}-${newStageNumber}`,
        source: `stage-${newStageNumber - 1}`,
        target: `stage-${newStageNumber}`,
        type: 'smoothstep',
        animated: true,
        markerEnd: {
          type: MarkerType.ArrowClosed
        }
      };
      setEdges(eds => [...eds, newEdge]);
    }
  }, [flow, onFlowChange, setNodes, setEdges]);

  const handleDeleteStage = useCallback((stageNumber) => {
    if (!window.confirm(`Delete Stage ${stageNumber}? This cannot be undone.`)) {
      return;
    }

    // Remove from flow
    const updatedStages = flow.stages
      .filter(s => s.stageNumber !== stageNumber)
      .map((s, index) => ({ ...s, stageNumber: index + 1 })); // Renumber

    onFlowChange({ ...flow, stages: updatedStages });

    // Remove from visual state
    setNodes(nds => nds.filter(n => n.id !== `stage-${stageNumber}`));
    setEdges(eds => eds.filter(e => !e.id.includes(`stage-${stageNumber}`)));

    // Close editor if this stage was selected
    if (selectedStage?.stageNumber === stageNumber) {
      setSelectedStage(null);
    }
  }, [flow, onFlowChange, setNodes, setEdges, selectedStage]);

  const handleStageChange = useCallback((updatedStage) => {
    const updatedStages = flow.stages.map(s =>
      s.stageNumber === updatedStage.stageNumber ? updatedStage : s
    );

    onFlowChange({ ...flow, stages: updatedStages });

    // Update visual state
    setNodes(nds => nds.map(node => {
      if (node.id === `stage-${updatedStage.stageNumber}`) {
        return {
          ...node,
          data: {
            ...node.data,
            stage: updatedStage
          }
        };
      }
      return node;
    }));

    setSelectedStage(updatedStage);
  }, [flow, onFlowChange, setNodes]);

  const handleNodeClick = useCallback((event, node) => {
    setSelectedStage(node.data.stage);
  }, []);

  // Update node data to include callbacks whenever stages change
  useEffect(() => {
    setNodes(nds => nds.map(node => ({
      ...node,
      data: {
        ...node.data,
        onEdit: () => setSelectedStage(node.data.stage),
        onDelete: () => handleDeleteStage(node.data.stage.stageNumber)
      }
    })));
  }, [flow.stages, handleDeleteStage, setNodes]);

  return (
    <Box sx={{ height: '70vh', display: 'flex', position: 'relative' }}>
      <Box sx={{ flex: selectedStage ? 2 : 1, border: '1px solid #ddd', borderRadius: 1, position: 'relative' }}>
        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ position: 'absolute', top: 10, left: 10, right: 10, zIndex: 10 }}>
            {validationErrors[0]} {validationErrors.length > 1 && `(+${validationErrors.length - 1} more)`}
          </Alert>
        )}

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.5}
          maxZoom={1.5}
          nodesDraggable={false}  // Disable dragging - visual position doesn't affect stage order
          nodesConnectable={false}  // Disable edge connections - order is determined by stageNumber
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: true,
            markerEnd: {
              type: MarkerType.ArrowClosed
            }
          }}
        >
          <Background color="#aaa" gap={16} />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              const stage = node.data?.stage;
              if (!stage) return '#ddd';
              const hasPersona = !!stage.personaId;
              const hasTransition = !!stage.transitionTrigger?.type;
              return hasPersona && hasTransition ? '#4caf50' : '#ff9800';
            }}
            maskColor="rgba(255, 255, 255, 0.8)"
          />
        </ReactFlow>

        <Box sx={{ position: 'absolute', bottom: 16, left: 16, zIndex: 10 }}>
          <Tooltip title="Add Stage">
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddStage}
              sx={{ boxShadow: 3 }}
            >
              Add Stage
            </Button>
          </Tooltip>
        </Box>

        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10, display: 'flex', gap: 1 }}>
          <Tooltip title="List View (Coming Soon)">
            <span>
              <IconButton disabled size="small" sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'white' } }}>
                <ListIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Canvas View (Current)">
            <IconButton size="small" sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <TreeIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {selectedStage && (
        <StageEditorPanel
          stage={selectedStage}
          onStageChange={handleStageChange}
          onClose={() => setSelectedStage(null)}
        />
      )}
    </Box>
  );
};

export default VisualFlowEditor;
